import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslate } from '@tolgee/react';
import SimpleNavbar from '../../components/Navbar';
import Header from './AwsGraphs/Header';
import PossibleSavingsBar from './AwsGraphs/PossibleSavingsBar';
import SavingsBreakdownPie from './AwsGraphs/SavingsBreakdownPie';
import ProjectedSavingsLine from './AwsGraphs/ProjectedSavingsLine';
import TopExpensesBar from './AwsGraphs/TopExpensesBar';

// Type for the response data
export interface AwsPdfResponse {
    total_bill: number;
    total_main_services?: number;
    possible_savings?: number;
    possible_savings_flat?: {
        on_demand_services: number;
        excess_data_transfer: number;
        taxes_paid_on_aws: number;
        payment_extended_support: number;
        client_pays_support: number;
        ebs_in_wrong_format: number;
        snapshots_size: number;
        s3_glacier: number;
        storage: number;
        idle_public_ips: number;
        ecr_images: number;
    };
    bill_with_savings: number;
    nota?: number;
    nota_benchmark?: number;
    has_credits: number;
    total_by_service: {
        item_total: {
            [key: string]: number;
        };
        total_main_services?: number;
    };
    data_overtime: {
        with_frust: number[];
        without_frust: number[];
    };
    inneficiencies: {
        on_demand_services: {
            inneficient: number;
            possible_savings: number;
            data: {
                total_services: number;
                inner_data: {
                    [key: string]: {
                        inneficient?: number;
                        total_service: number;
                        total_covered?: number;
                        total_coverable?: number;
                        total_instances?: number;
                        savings_total?: number;
                        savings_percentage?: number;
                        possible_savings: number;
                    };
                };
            };
        };
        excess_data_transfer: {
            inneficient: number;
            possible_savings: number;
            data: {
                total_data_transfer: number;
                cloud_front_total: number;
                cf_percentage: number;
                inneficiencies: number;
            };
        };
        taxes_paid_on_aws: {
            inneficient: number;
            possible_savings: number;
        };
        payment_extended_support?: {
            inneficient: number;
            possible_savings: number;
        };
        client_pays_support?: {
            inneficient: number;
            possible_savings: number;
        };
        ebs_in_wrong_format: {
            inneficient: number;
            possible_savings: number;
            data: {
                total_ebs: number;
                snapshot: number;
                io1: number;
                io2: number;
                gp2: number;
                gp3: number;
            };
        };
        snapshots_size?: {
            inneficient: number;
            possible_savings: number;
            data: {
                total_snapshot: number;
                total_rds: number;
                total_s3: number;
            };
        };
        s3_glacier?: {
            inneficient: number;
            possible_savings: number;
            data: {
                total_s3: number;
                total_glacier: number;
                glacier_percentage: number;
            };
        };
        storage?: {
            inneficient: number;
            possible_savings: number;
            data: {
                total_s3: number;
                total_main_services: number;
            };
        };
        idle_public_ips?: {
            inneficient: number;
            possible_savings: number;
        };
        ecr_images?: {
            inneficient: number;
            possible_savings: number;
            data: {
                total_ecr: number;
                total_ecs: number;
                ecr_percentage: number;
            };
        };
    };
}

const AwsGraphs: React.FC<{ data?: AwsPdfResponse; loading: boolean; error: Error | null }> = ({ data, loading, error }) => {
    const { t } = useTranslate();

    if (loading)
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    gap: 10
                }}
            >
                <CircularProgress color="inherit" />
                <Typography variant="h4" color="textSecondary">
                    {t('pdf_success.loading')}
                </Typography>
            </div>
        );
    if (error)
        return (
            <Box>
                {t('pdf_success.error')}
                {error.message}
            </Box>
        );
    if (!data) return null;

    console.log(data);

    return (
        <>
            <SimpleNavbar />
            <Box sx={{ display: 'flex', justifyContent: 'center', backgroundColor: '#feeeff' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        px: { xs: 4, md: 10 },
                        py: 5,
                        maxWidth: '72rem',
                        width: '100%'
                    }}
                >
                    {/* Header Section */}
                    <Header data={data} />

                    {/* Charts 1-2: Responsive Grid layout */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 5
                        }}
                    >
                        {/* Chart 1: Possible Savings*/}
                        <PossibleSavingsBar data={data} />
                        {/* Chart 2: Possible Savings Breakdown */}
                        <SavingsBreakdownPie data={data} />
                    </Box>

                    {/* Projected Savings Over Time */}
                    <ProjectedSavingsLine data={data} />

                    {/* Top 10 Expenses */}
                    <TopExpensesBar data={data} />
                </Box>
            </Box>
        </>
    );
};

export default AwsGraphs;
